import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { CountryToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useCountryRegionMutations } from '~/hooks/use-country-regions';
import { countryRegionSchema, EMPTY_COUNTRY_REGION, type CountryRegionFormValues } from '~/lib/schemas/country-region';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: CountryRegionFormValues | null;
	countries: CountryToken[];
}

/** Add / edit a region (Νομός). RHF + zod, with a country (`cid`) dropdown. */
export function CountryRegionEditDialog({ open, onOpenChange, initial, countries }: Props) {
	const { t } = useTranslation();
	const { create, update } = useCountryRegionMutations();
	const form = useForm<CountryRegionFormValues>({
		resolver: zodResolver(countryRegionSchema) as Resolver<CountryRegionFormValues>,
		defaultValues: initial ?? EMPTY_COUNTRY_REGION,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_COUNTRY_REGION);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const cid = watch('cid');
	const visible = watch('iv');

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('system.countryRegions.editTitle') : t('system.countryRegions.addTitle')}</DialogTitle>
					<DialogDescription>{t('system.countryRegions.formSubtitle')}</DialogDescription>
				</DialogHeader>

				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('system.countryRegions.columns.country')} error={formState.errors.cid?.message}>
						<Select
							value={cid ? String(cid) : ''}
							onValueChange={v => setValue('cid', Number(v), { shouldDirty: true, shouldValidate: true })}>
							<SelectTrigger className="w-full" aria-label={t('system.countryRegions.columns.country')}>
								<SelectValue placeholder={t('system.countryRegions.countryPlaceholder')} />
							</SelectTrigger>
							<SelectContent>
								{countries.map(c => (
									<SelectItem key={c.id} value={String(c.id)}>
										{c.nm}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</FormField>
					<FormField label={t('system.countryRegions.columns.name')} error={formState.errors.nm?.message}>
						<Input {...register('nm')} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
					</FormField>
					<div className="grid grid-cols-2 gap-4">
						<FormField label={t('system.countryRegions.columns.code')} error={formState.errors.cd?.message}>
							<Input {...register('cd')} maxLength={50} aria-invalid={Boolean(formState.errors.cd)} />
						</FormField>
						<FormField label={t('system.countryRegions.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
					</div>
					<FormField label={t('system.fields.erpCode')}>
						<Input {...register('erpcd')} />
					</FormField>
					<div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
						<Label htmlFor="region-visible">{t('system.countryRegions.columns.visible')}</Label>
						<Switch id="region-visible" checked={visible} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
					</div>

					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
